/**
 * pdfUtils.js — Design system compartido para todos los PDFs del sistema
 * Importar: import { pdfHeader, pdfFooter, pdfTableStyles, fmtMXN } from "../../../pdfUtils";
 * (ajustar la ruta relativa según la profundidad del componente)
 */

// ─── Paleta ───────────────────────────────────────────────────────────────────
const C = {
  dark:    [15,  23,  42],   // #0f172a  — encabezado tabla / títulos
  medium:  [71,  85, 105],   // #475569  — subtítulos / footer
  light:   [248, 250, 252],  // #f8fafc  — filas alternas
  border:  [226, 232, 240],  // #e2e8f0  — líneas
  foot:    [241, 245, 249],  // #f1f5f9  — fila de totales
  white:   [255, 255, 255],
  text:    [15,  23,  42],
  muted:   [148, 163, 184],  // #94a3b8
};

// ─── Formateador ──────────────────────────────────────────────────────────────
export const fmtMXN = (v) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v) || 0);

// ─── Header ───────────────────────────────────────────────────────────────────
/**
 * Dibuja el encabezado del documento y devuelve el startY sugerido para la tabla.
 * @param {jsPDF} doc
 * @param {string} title        — Título principal, e.g. "Listado de Cargos"
 * @param {string} [subtitle]   — Subtítulo opcional, e.g. "Enero – Mayo 2026"
 * @returns {number} startY recomendado para el autoTable
 */
export function pdfHeader(doc, title, subtitle = "") {
  const W = doc.internal.pageSize.getWidth();

  // Logo institución — izquierda
  try {
    const imgInst = new Image();
    imgInst.src = `${process.env.PUBLIC_URL}/LogoInstitucion.png`;
    doc.addImage(imgInst, "PNG", 12, 8, 26, 26);
  } catch (_) { /* si no existe la imagen, continúa */ }

  // Logo Lynqro — derecha
  try {
    const imgLynqro = new Image();
    imgLynqro.src = `${process.env.PUBLIC_URL}/Logo-Lynqro-PDF.png`;
    doc.addImage(imgLynqro, "PNG", W - 48, 10, 38, 13);
  } catch (_) { /* continúa */ }

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...C.dark);
  doc.text(title, W / 2, 19, { align: "center" });

  // Subtítulo
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...C.medium);
    doc.text(subtitle, W / 2, 27, { align: "center" });
  }

  // Línea divisoria
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.5);
  doc.line(12, 37, W - 12, 37);

  return subtitle ? 42 : 40;
}

// ─── Footer con paginación ────────────────────────────────────────────────────
/**
 * Añade pie de página con fecha y número de página a TODAS las páginas.
 * Llamar DESPUÉS de doc.autoTable y ANTES de doc.save().
 */
export function pdfFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const fecha = new Date().toLocaleDateString("es-MX", {
    day: "2-digit", month: "long", year: "numeric",
  });

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text(`Generado: ${fecha}`, 12, H - 7);
    doc.text(`Página ${i} de ${pageCount}`, W - 12, H - 7, { align: "right" });
    // línea superior del footer
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.line(12, H - 12, W - 12, H - 12);
  }
}

// ─── Estilos de tabla ─────────────────────────────────────────────────────────
/**
 * Opciones base para doc.autoTable({ ...pdfTableStyles(), ... })
 * Se pueden sobreescribir individualmente.
 */
export function pdfTableStyles(extraHeadStyles = {}, extraBodyStyles = {}) {
  return {
    theme: "grid",
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 3, right: 5, bottom: 3, left: 5 },
      font: "helvetica",
      textColor: C.text,
      lineColor: C.border,
      lineWidth: 0.25,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: C.dark,
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 8,
      halign: "center",
      cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
      ...extraHeadStyles,
    },
    alternateRowStyles: {
      fillColor: C.light,
    },
    footStyles: {
      fillColor: C.foot,
      textColor: C.dark,
      fontStyle: "bold",
      fontSize: 8.5,
    },
    margin: { left: 12, right: 12 },
    ...extraBodyStyles,
  };
}
